import type { PointLabelPosition } from '@examdojo/core/widget';
import type { AxisMarkerVariant, TrigLine } from './unit-circle.model';

// =============================================================================
// CONSTANTS
// =============================================================================

const TWO_PI = 2 * Math.PI;

/** The 16 standard angles in radians (0 to 11π/6). */
const SPECIAL_ANGLES: readonly number[] = [
  0,
  Math.PI / 6,
  Math.PI / 4,
  Math.PI / 3,
  Math.PI / 2,
  (2 * Math.PI) / 3,
  (3 * Math.PI) / 4,
  (5 * Math.PI) / 6,
  Math.PI,
  (7 * Math.PI) / 6,
  (5 * Math.PI) / 4,
  (4 * Math.PI) / 3,
  (3 * Math.PI) / 2,
  (5 * Math.PI) / 3,
  (7 * Math.PI) / 4,
  (11 * Math.PI) / 6,
];

/** Exact coordinate labels (KaTeX) for the 16 special angles on the unit circle. */
const SPECIAL_ANGLE_COORD_LABELS: readonly string[] = [
  '$(1,\\, 0)$',
  '$\\left(\\frac{\\sqrt{3}}{2},\\, \\frac{1}{2}\\right)$',
  '$\\left(\\frac{\\sqrt{2}}{2},\\, \\frac{\\sqrt{2}}{2}\\right)$',
  '$\\left(\\frac{1}{2},\\, \\frac{\\sqrt{3}}{2}\\right)$',
  '$(0,\\, 1)$',
  '$\\left(-\\frac{1}{2},\\, \\frac{\\sqrt{3}}{2}\\right)$',
  '$\\left(-\\frac{\\sqrt{2}}{2},\\, \\frac{\\sqrt{2}}{2}\\right)$',
  '$\\left(-\\frac{\\sqrt{3}}{2},\\, \\frac{1}{2}\\right)$',
  '$(-1,\\, 0)$',
  '$\\left(-\\frac{\\sqrt{3}}{2},\\, -\\frac{1}{2}\\right)$',
  '$\\left(-\\frac{\\sqrt{2}}{2},\\, -\\frac{\\sqrt{2}}{2}\\right)$',
  '$\\left(-\\frac{1}{2},\\, -\\frac{\\sqrt{3}}{2}\\right)$',
  '$(0,\\, -1)$',
  '$\\left(\\frac{1}{2},\\, -\\frac{\\sqrt{3}}{2}\\right)$',
  '$\\left(\\frac{\\sqrt{2}}{2},\\, -\\frac{\\sqrt{2}}{2}\\right)$',
  '$\\left(\\frac{\\sqrt{3}}{2},\\, -\\frac{1}{2}\\right)$',
];

/** Angle display labels in radians (KaTeX) for the 16 special angles. */
const SPECIAL_ANGLE_RAD_LABELS: readonly string[] = [
  '$0$',
  '$\\frac{\\pi}{6}$',
  '$\\frac{\\pi}{4}$',
  '$\\frac{\\pi}{3}$',
  '$\\frac{\\pi}{2}$',
  '$\\frac{2\\pi}{3}$',
  '$\\frac{3\\pi}{4}$',
  '$\\frac{5\\pi}{6}$',
  '$\\pi$',
  '$\\frac{7\\pi}{6}$',
  '$\\frac{5\\pi}{4}$',
  '$\\frac{4\\pi}{3}$',
  '$\\frac{3\\pi}{2}$',
  '$\\frac{5\\pi}{3}$',
  '$\\frac{7\\pi}{4}$',
  '$\\frac{11\\pi}{6}$',
];

/** Angle display labels in degrees (KaTeX) for the 16 special angles. */
const SPECIAL_ANGLE_DEG_LABELS: readonly string[] = [
  '$0°$',
  '$30°$',
  '$45°$',
  '$60°$',
  '$90°$',
  '$120°$',
  '$135°$',
  '$150°$',
  '$180°$',
  '$210°$',
  '$225°$',
  '$240°$',
  '$270°$',
  '$300°$',
  '$315°$',
  '$330°$',
];

/**
 * Label position for each of the 16 special angles, placed outward from the circle.
 * Avoids overlapping with axes and the circle itself.
 */
const SPECIAL_ANGLE_LABEL_POSITIONS: readonly PointLabelPosition[] = [
  'right',
  'topRight',
  'topRight',
  'topRight',
  'top',
  'topLeft',
  'topLeft',
  'topLeft',
  'left',
  'bottomLeft',
  'bottomLeft',
  'bottomLeft',
  'bottom',
  'bottomRight',
  'bottomRight',
  'bottomRight',
];

/** Axis marker labels (KaTeX): (1,0), (0,1), (−1,0), (0,−1). */
const AXIS_MARKER_COORDS: readonly string[] = ['$(1,\\, 0)$', '$(0,\\, 1)$', '$(-1,\\, 0)$', '$(0,\\, -1)$'];

/** Axis marker labels (KaTeX): 0, π/2, π, 3π/2. */
const AXIS_MARKER_RADIANS: readonly string[] = ['$0$', '$\\frac{\\pi}{2}$', '$\\pi$', '$\\frac{3\\pi}{2}$'];

/** Axis marker labels (KaTeX): 0°, 90°, 180°, 270°. */
const AXIS_MARKER_DEGREES: readonly string[] = ['$0°$', '$90°$', '$180°$', '$270°$'];

/** Label positions for the 4 axis markers (right, top, left, bottom). */
const AXIS_MARKER_POSITIONS: readonly PointLabelPosition[] = ['right', 'top', 'left', 'bottom'];

/** Angles for the 4 axis markers: 0, π/2, π, 3π/2. */
const AXIS_MARKER_ANGLES: readonly number[] = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];

// =============================================================================
// PUBLIC API
// =============================================================================

/** Returns the 16 standard angles in radians. */
export function getSpecialAngles(): readonly number[] {
  return SPECIAL_ANGLES;
}

/** Returns the exact coordinate label (KaTeX) for a special angle index (0–15). */
export function getSpecialAngleCoordLabel(index: number): string {
  return SPECIAL_ANGLE_COORD_LABELS[index] ?? '';
}

/** Returns the angle label string (KaTeX) for a special angle index. */
export function getSpecialAngleAngleLabel(index: number, unit: 'deg' | 'rad'): string {
  return unit === 'deg' ? SPECIAL_ANGLE_DEG_LABELS[index] ?? '' : SPECIAL_ANGLE_RAD_LABELS[index] ?? '';
}

/** Returns the optimal label position for a special angle by index. */
export function getSpecialAngleLabelPosition(index: number): PointLabelPosition {
  return SPECIAL_ANGLE_LABEL_POSITIONS[index] ?? 'topRight';
}

/** Returns axis marker data for a given variant. */
export function getAxisMarkerData(variant: AxisMarkerVariant): ReadonlyArray<{
  angle: number;
  label: string;
  position: PointLabelPosition;
}> {
  if (variant === 'none') {
    return [];
  }

  const labels =
    variant === 'coordinates' ? AXIS_MARKER_COORDS : variant === 'radians' ? AXIS_MARKER_RADIANS : AXIS_MARKER_DEGREES;

  return AXIS_MARKER_ANGLES.map((angle, i) => ({
    angle,
    label: labels[i],
    position: AXIS_MARKER_POSITIONS[i],
  }));
}

/** Finds the special angle index for a given radian value, or -1 if not found. */
export function findSpecialAngleIndex(angle: number): number {
  const normalized = normalizeAngle(angle);
  for (let i = 0; i < SPECIAL_ANGLES.length; i++) {
    if (Math.abs(normalized - SPECIAL_ANGLES[i]) < 0.001) {
      return i;
    }
  }
  return -1;
}

/** Compute trig line segment endpoints for a given angle and radius. */
export function computeTrigLineEndpoints(
  angle: number,
  radius: number,
): Record<TrigLine, { from: [number, number]; to: [number, number] }> {
  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);
  const tanA = Math.tan(angle);
  const r = radius;

  return {
    sin: { from: [r * cosA, 0], to: [r * cosA, r * sinA] },
    cos: { from: [0, 0], to: [r * cosA, 0] },
    tan: { from: [r, 0], to: [r, r * tanA] },
    cot: { from: [0, r], to: [r * (cosA / sinA), r] },
    sec: { from: [0, 0], to: [r / cosA, 0] },
    csc: { from: [0, 0], to: [0, r / sinA] },
  };
}

/** Returns the quadrant (1–4) for an angle in radians. */
export function getQuadrant(angle: number): 1 | 2 | 3 | 4 {
  const normalized = normalizeAngle(angle);
  if (normalized < Math.PI / 2) {
    return 1;
  }
  if (normalized < Math.PI) {
    return 2;
  }
  if (normalized < (3 * Math.PI) / 2) {
    return 3;
  }
  return 4;
}

/** Returns the sign (+/−) of sin and cos for the given angle. */
export function getSignInfo(angle: number): { sin: '+' | '-'; cos: '+' | '-' } {
  const q = getQuadrant(angle);
  return {
    sin: q <= 2 ? '+' : '-',
    cos: q === 1 || q === 4 ? '+' : '-',
  };
}

/** Normalize an angle to [0, 2π). */
export function normalizeAngle(radians: number): number {
  let a = radians % TWO_PI;
  if (a < 0) {
    a += TWO_PI;
  }
  return a;
}

/**
 * Parse an angle from a number or human-readable string.
 *
 * Accepted formats:
 * - Number: returned as-is
 * - `'\\pi/6'`, `'2\\pi/3'`, `'\\pi'` — LaTeX pi fractions
 * - `'45°'`, `'180°'` — degrees
 * - `'1.5'` — plain numeric string (radians)
 */
export function parseAngle(input: number | string): number {
  if (typeof input === 'number') {
    return input;
  }

  const trimmed = input.trim();

  // Degrees: '45°'
  const degMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*°$/);
  if (degMatch) {
    return (parseFloat(degMatch[1]) * Math.PI) / 180;
  }

  // \pi-based: '\pi', '\pi/6', '2\pi/3', '-\pi/4', '11\pi/6'
  const piMatch = trimmed.match(/^(-?\d*)\s*\\pi\s*(?:\/\s*(\d+))?$/);
  if (piMatch) {
    const raw = piMatch[1];
    const coeff = raw === '' || raw === undefined ? 1 : raw === '-' ? -1 : parseInt(raw, 10);
    const denom = piMatch[2] ? parseInt(piMatch[2], 10) : 1;
    return (coeff * Math.PI) / denom;
  }

  // Plain number string
  const num = parseFloat(trimmed);
  if (!isNaN(num)) {
    return num;
  }

  return 0;
}

/** Returns the optimal label position for a trig line based on the current angle. */
export function getTrigLineLabelPosition(tl: TrigLine, angle: number): 'top' | 'bottom' | 'left' | 'right' {
  const q = getQuadrant(angle);
  switch (tl) {
    case 'sin':
      return q === 1 || q === 4 ? 'right' : 'left';
    case 'cos':
      return q === 1 || q === 2 ? 'top' : 'bottom';
    case 'tan':
      return 'right';
    case 'sec':
      return q === 1 || q === 2 ? 'top' : 'bottom';
    case 'csc':
      return q === 1 || q === 4 ? 'right' : 'left';
    case 'cot':
      return 'top';
  }
}

/** Find the nearest special angle (always returns a result). */
export function findNearestSpecialAngle(angle: number): number {
  const normalized = normalizeAngle(angle);
  let bestAngle = SPECIAL_ANGLES[0];
  let bestDist = Infinity;
  for (const special of SPECIAL_ANGLES) {
    const dist = Math.min(Math.abs(normalized - special), TWO_PI - Math.abs(normalized - special));
    if (dist < bestDist) {
      bestDist = dist;
      bestAngle = special;
    }
  }
  return bestAngle;
}

/** Find the nearest special angle if within a tolerance, or return null if no snap. */
export function snapToNearestSpecialAngle(angle: number, tolerance = 0.1): number | null {
  const normalized = normalizeAngle(angle);
  for (const special of SPECIAL_ANGLES) {
    if (Math.abs(normalized - special) < tolerance) {
      return special;
    }
  }
  // Also check wrap-around (near 2π → snap to 0)
  if (TWO_PI - normalized < tolerance) {
    return 0;
  }
  return null;
}
